import React from "react";
import { Drawer, Table } from "antd";

const BOMStockDrawer = ({ open, onClose, stockData, precision, notFoundImg }) => {
  const insufficientItems =
    stockData?.filter((item) => item.availabilityStatus !== "Yes" && item.availableQty < item.orderQty) || [];

  // Backend's billOfMaterials currently returns only {value, qtyOnHand} per component (no
  // per-component required/recipe qty). Read a required-qty field defensively so this column
  // lights up automatically once the backend adds one, without breaking on its absence today.
  const getBomRequiredQty = (bom) => bom.requiredQty ?? bom.reqQty ?? bom.requiredStock ?? bom.qtyRequired ?? null;

  const allOutOfStock = insufficientItems.length > 0 && insufficientItems.every((item) => Number(item.availableQty) <= 0);
  const hasAnyBomData = insufficientItems.some((item) => item.billOfMaterials?.length > 0);

  return (
    <Drawer
      open={open && insufficientItems.length > 0}
      onClose={onClose}
      placement="bottom"
      closable={false}
      height="80vh"
      mask={true}
      maskClosable={false}
      styles={{
        mask: { background: "#000000a6" },
        body: {
          padding: "5px 30px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        },
      }}
      style={{ textAlign: "center" }}
    >
      <div
        style={{
          fontWeight: "500",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "1vw",
        }}
      >
        <img src={notFoundImg} alt="Stock Unavailable" style={{ width: "60px", height: "50px", marginTop: "5px" }} />
        <p style={{ margin: "2px 0", fontSize: "1.8em", fontWeight: "600", color: "#0F0718" }}>
          {allOutOfStock ? "Stock is not available" : "Insufficient stock for the requested quantity"}
        </p>
        <p style={{ color: "#6c757d", fontWeight: 400, fontSize: "1.1em" }}>
          {allOutOfStock
            ? hasAnyBomData
              ? "Add sufficient stock for the below BOM Item(s) to proceed billing"
              : "Add sufficient stock for the below item(s) to proceed billing"
            : hasAnyBomData
              ? "At least the available quantity shown below can be billed — add sufficient stock for the below BOM Item(s) to bill the full requested quantity"
              : "At least the available quantity shown below can be billed — add sufficient stock to bill the full requested quantity"}
        </p>
      </div>
      <Table
        className="sm-stock-check-table"
        pagination={false}
        style={{ height: "50vh" }}
        scroll={{ y: "43vh" }}
        columns={[
          {
            title: "Item Name",
            dataIndex: "name",
            key: "name",
            width: hasAnyBomData ? "18%" : "30%",
            render: (text, record) => (
              <div>
                <div style={{ fontWeight: 500 }}>{text}</div>
                <div style={{ fontSize: "12px", color: "#888" }}>{record.productId}</div>
              </div>
            ),
          },
          // Only relevant for BOM/make-to-order products — omit entirely for a batch of plain products.
          ...(hasAnyBomData
            ? [
                {
                  title: "Insufficient BOM Item(s)",
                  key: "bom",
                  width: "22%",
                  render: (_, record) => {
                    if (!record.billOfMaterials?.length) return "-";
                    // BUG-2405: flag the component that limits the quantity -- lowest
                    // qtyOnHand/requiredQty when the backend sends a recipe qty, otherwise
                    // the lowest qtyOnHand ("Lowest stock", since the recipe ratio is unknown).
                    const hasRequired = record.billOfMaterials.every((bom) => Number(getBomRequiredQty(bom)) > 0);
                    const coverage = (bom) => Number(bom.qtyOnHand || 0) / (hasRequired ? Number(getBomRequiredQty(bom)) : 1);
                    const minCoverage = Math.min(...record.billOfMaterials.map(coverage));
                    return record.billOfMaterials.map((bom, i) => {
                      const requiredQty = getBomRequiredQty(bom);
                      const isLimiting = record.billOfMaterials.length === 1 || coverage(bom) === minCoverage;
                      return (
                        <div key={i} style={{ fontSize: "12px", marginBottom: i < record.billOfMaterials.length - 1 ? "4px" : 0 }}>
                          <span style={{ fontWeight: 500 }}>{bom.name || bom.value}</span>
                          {bom.name && <span style={{ color: "#888" }}> ({bom.value})</span>}
                          <span style={{ color: "#f5222d" }}> · Available: {Number(bom.qtyOnHand || 0).toFixed(precision)}</span>
                          {requiredQty !== null && <span style={{ color: "#6c757d" }}> · Required: {Number(requiredQty).toFixed(precision)}</span>}
                          {isLimiting && (
                            <span style={{ color: "#f5222d", fontWeight: 600 }}> · {hasRequired || record.billOfMaterials.length === 1 ? "Limiting" : "Lowest stock"}</span>
                          )}
                        </div>
                      );
                    });
                  },
                },
              ]
            : []),
          { title: "Requested Qty", dataIndex: "orderQty", key: "orderQty", width: hasAnyBomData ? "13%" : "16%" },
          { title: "Available Qty", dataIndex: "availableQty", key: "availableQty", width: hasAnyBomData ? "13%" : "16%" },
          {
            title: "Required Qty",
            key: "shortfall",
            width: hasAnyBomData ? "15%" : "18%",
            render: (_, record) => {
              const diff = record.orderQty - record.availableQty;
              return <span style={{ color: "#f5222d", fontWeight: 600 }}>{Math.abs(diff).toFixed(precision)}</span>;
            },
          },
          {
            title: "Status",
            key: "status",
            width: hasAnyBomData ? "19%" : "20%",
            render: (_, record) => {
              const available = Number(record.availableQty) || 0;
              if (available <= 0) {
                return <span style={{ color: "#f5222d", fontWeight: 600 }}>Out of Stock</span>;
              }
              // availableQty on the failure path can under-report true sellable qty (BOM producibility
              // isn't always reflected here — see the payload trace in TSK-2981), so this is a floor,
              // not an asserted ceiling.
              const suffix = hasAnyBomData ? "add stock for the BOM item(s) above" : "add sufficient stock";
              return <span style={{ color: "#d48806" }}>{`At least ${available.toFixed(precision)} available — ${suffix} to bill the full quantity`}</span>;
            },
          },
        ]}
        dataSource={insufficientItems}
        rowKey="productId"
      />
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "2vh", paddingRight: "1px" }}>
        <button
          style={{
            width: "25%",
            padding: "6px 12px",
            backgroundColor: "#2F3856",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
          onClick={onClose}
        >
          OK
        </button>
      </div>
    </Drawer>
  );
};

export default BOMStockDrawer;
